import { useState } from 'react';
import Select, { SelectOption } from '@/components/common/Select';
import CityCascader from '@/components/common/CityCascader';
import ImageUploadBox from '@/components/common/ImageUploadBox';
import SendCodeButton from '@/components/common/SendCodeButton';
import {
  fetchSendVerificationChallenge,
  fetchVerifyVerificationChallenge,
} from '@/service/api/verification';
import { CURRENCY_OPTIONS } from '@/utils/currency';
import { EditableFormData, EditablePortfolioItem } from './types';

const SERVICE_MODE_OPTIONS: SelectOption[] = [
  { label: '全职合作', value: 'FULL_TIME' },
  { label: '兼职驻场', value: 'PART_TIME' },
  { label: '按项目交付', value: 'PROJECT' },
];

const PORTFOLIO_CATEGORY_OPTIONS: SelectOption[] = [
  { label: '图片作品', value: 'PORTFOLIO_IMAGE' },
  { label: '视频作品', value: 'PORTFOLIO_VIDEO' },
];

interface ProviderProfileEditFormProps {
  formData: EditableFormData;
  setFormData: React.Dispatch<React.SetStateAction<EditableFormData>>;
  categories: Api.BusinessCategory.BusinessCategoryResponse[];
  currentDisplay: Api.Provider.ProviderProfileResponse | null;
  tagInput: string;
  setTagInput: (val: string) => void;
  handleAddTag: () => void;
  handleRemoveTag: (tag: string) => void;
  handleToggleCategory: (code: string) => void;
  handleAddPortfolioItem: () => void;
  handleRemovePortfolioItem: (index: number) => void;
  handlePortfolioChange: (index: number, field: 'name' | 'intro', value: string) => void;
  handleSaveDraft: () => void;
  handleSubmitProfile: () => void;
  onCancel: () => void;
  savingDraft: boolean;
  submitting: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  onOpenAgreement?: () => void;
}

export default function ProviderProfileEditForm({
  formData,
  setFormData,
  categories,
  currentDisplay,
  tagInput,
  setTagInput,
  handleAddTag,
  handleRemoveTag,
  handleToggleCategory,
  handleAddPortfolioItem,
  handleRemovePortfolioItem,
  handlePortfolioChange,
  handleSaveDraft,
  handleSubmitProfile,
  onCancel,
  savingDraft,
  submitting,
  showToast,
  onOpenAgreement,
}: ProviderProfileEditFormProps) {
  const [phoneCode, setPhoneCode] = useState('');
  const [verifyingPhone, setVerifyingPhone] = useState(false);

  const [emailCode, setEmailCode] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  // 发送手机验证码 (POST /verification-challenges)
  const handleSendPhoneCode = async (): Promise<boolean> => {
    const phone = formData.phone?.trim();
    if (!phone) {
      showToast('请先输入联系电话', 'warning');
      return false;
    }
    try {
      await fetchSendVerificationChallenge({
        purpose: 'PROVIDER_PROFILE_CONTACT_PHONE',
        target: phone,
      });
      showToast('手机验证码已发送，请查收', 'success');
      return true;
    } catch (err: any) {
      showToast(err instanceof Error ? err.message : '发送手机验证码失败', 'error');
      return false;
    }
  };

  // 校验手机验证码并换取 verification_token (POST /verification-challenges/verify)
  const handleVerifyPhoneCode = async () => {
    const phone = formData.phone?.trim();
    const code = phoneCode.trim();
    if (!phone) {
      showToast('请先输入联系电话', 'warning');
      return;
    }
    if (!code) {
      showToast('请输入手机验证码', 'warning');
      return;
    }
    setVerifyingPhone(true);
    try {
      const res = await fetchVerifyVerificationChallenge({
        purpose: 'PROVIDER_PROFILE_CONTACT_PHONE',
        target: phone,
        code,
      });
      if (res?.verification_token) {
        setFormData((prev) => ({
          ...prev,
          phone_verification_token: res.verification_token,
        }));
        showToast('手机号验证成功！已完成 Token 绑定', 'success');
      } else {
        showToast('获取验证 Token 失败，请重试', 'error');
      }
    } catch (err: any) {
      showToast(err instanceof Error ? err.message : '验证码校验失败', 'error');
    } finally {
      setVerifyingPhone(false);
    }
  };

  // 发送邮箱验证码 (POST /verification-challenges)
  const handleSendEmailCode = async (): Promise<boolean> => {
    const email = formData.email?.trim();
    if (!email) {
      showToast('请先输入联系邮箱', 'warning');
      return false;
    }
    try {
      await fetchSendVerificationChallenge({
        purpose: 'PROVIDER_PROFILE_CONTACT_EMAIL',
        target: email,
      });
      showToast('邮箱验证码已发送，请查收', 'success');
      return true;
    } catch (err: any) {
      showToast(err instanceof Error ? err.message : '发送邮箱验证码失败', 'error');
      return false;
    }
  };

  // 校验邮箱验证码并换取 verification_token (POST /verification-challenges/verify)
  const handleVerifyEmailCode = async () => {
    const email = formData.email?.trim();
    const code = emailCode.trim();
    if (!email) {
      showToast('请先输入联系邮箱', 'warning');
      return;
    }
    if (!code) {
      showToast('请输入邮箱验证码', 'warning');
      return;
    }
    setVerifyingEmail(true);
    try {
      const res = await fetchVerifyVerificationChallenge({
        purpose: 'PROVIDER_PROFILE_CONTACT_EMAIL',
        target: email,
        code,
      });
      if (res?.verification_token) {
        setFormData((prev) => ({
          ...prev,
          email_verification_token: res.verification_token,
        }));
        showToast('邮箱验证成功！已完成 Token 绑定', 'success');
      } else {
        showToast('获取验证 Token 失败，请重试', 'error');
      }
    } catch (err: any) {
      showToast(err instanceof Error ? err.message : '验证码校验失败', 'error');
    } finally {
      setVerifyingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 模块1：基本形象与展示名称 */}
      <div className="p-5 sm:p-6 rounded-2xl bg-white border border-gray-100 shadow-2xs space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
          <div className="w-7 h-7 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center font-semibold text-sm">
            <i className="ri-user-settings-line"></i>
          </div>
          <h2 className="text-sm font-bold text-foreground-900">基本形象与展示</h2>
        </div>

        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="w-full md:w-56 shrink-0 space-y-1.5">
            <label className="block text-xs font-semibold text-foreground-700">
              形象头像 <span className="text-foreground-400 font-normal">(建议 1:1 比例)</span>
            </label>
            <ImageUploadBox
              label="点击上传头像"
              scene="PROFILE"
              value={formData._tempAvatarUrl || formData.avatar_asset_id || currentDisplay?.avatar?.access_url || ''}
              onChange={(assetId, file) => {
                setFormData((prev) => ({
                  ...prev,
                  avatar_asset_id: assetId || undefined,
                  _tempAvatarUrl: file ? URL.createObjectURL(file) : undefined,
                }));
              }}
            />
          </div>

          <div className="flex-1 w-full space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                对外展示名称 <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={formData.display_name || ''}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="请输入您的对外展示姓名或团队名称"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 text-xs text-foreground-950 placeholder-foreground-400 outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all h-[42px]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                服务合作方式 <span className="text-rose-500">*</span>
              </label>
              <Select
                options={SERVICE_MODE_OPTIONS}
                value={formData.service_mode || 'FULL_TIME'}
                onChange={(val) => setFormData((prev) => ({ ...prev, service_mode: String(val) }))}
                size="sm"
                selectClassName="rounded-xl text-xs h-[42px] border-gray-200 bg-gray-50/50 focus:bg-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 模块2：定价与地域要求 */}
      <div className="p-5 sm:p-6 rounded-2xl bg-white border border-gray-100 shadow-2xs space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
          <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-semibold text-sm">
            <i className="ri-money-cny-box-line"></i>
          </div>
          <h2 className="text-sm font-bold text-foreground-900">定价与地域工作偏好</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">日薪 / 预算标准</label>
            <div className="flex gap-2">
              <div className="w-28 shrink-0">
                <Select
                  options={CURRENCY_OPTIONS}
                  value={formData.currency || 'CNY'}
                  onChange={(val) => setFormData((prev) => ({ ...prev, currency: String(val) }))}
                  size="sm"
                  selectClassName="rounded-xl text-xs h-[38px] border-gray-200 bg-gray-50/50"
                />
              </div>
              <input
                type="number"
                value={formData.daily_rate ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    daily_rate: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                placeholder="日薪 (如: 800)"
                className="flex-1 min-w-0 px-3.5 py-2 rounded-xl border border-gray-200 bg-gray-50/50 text-xs text-foreground-950 placeholder-foreground-400 outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all h-[38px]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">常驻城市</label>
            <CityCascader
              value={formData.city_code}
              onChange={(cityId) => setFormData((prev) => ({ ...prev, city_code: cityId ? String(cityId) : '' }))}
              placeholder="请选择常驻城市"
              clearable
              selectClassName="h-[38px] px-3 rounded-xl text-xs border-gray-200 bg-gray-50/50"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">接单与履约偏好</label>
            <div className="flex items-center gap-4 h-[38px] px-3 rounded-xl bg-gray-50/60 border border-gray-100">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-foreground-800 shrink-0">
                <input
                  type="checkbox"
                  checked={formData.remote ?? true}
                  onChange={(e) => setFormData({ ...formData, remote: e.target.checked })}
                  className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                支持远程
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-foreground-800 shrink-0">
                <input
                  type="checkbox"
                  checked={formData.seeking_work ?? true}
                  onChange={(e) => setFormData({ ...formData, seeking_work: e.target.checked })}
                  className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                开放接单
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* 模块3：业务类目与技能标签 */}
      <div className="p-5 sm:p-6 rounded-2xl bg-white border border-gray-100 shadow-2xs space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-semibold text-sm">
            <i className="ri-award-line"></i>
          </div>
          <h2 className="text-sm font-bold text-foreground-900">擅长业务领域与技能标签</h2>
        </div>

        {/* 业务类目 */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-2">
            擅长业务类目 <span className="text-foreground-400 font-normal">(多选)</span>
          </label>
          <div className="flex flex-wrap gap-2 p-3.5 rounded-xl bg-gray-50/70 border border-gray-100 max-h-44 overflow-y-auto">
            {categories.map((cat) => {
              const code = cat.code || String(cat.id);
              const selected = (formData.category_codes || []).includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => handleToggleCategory(code)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 ${
                    selected
                      ? 'bg-primary-600 text-white shadow-2xs scale-[1.02]'
                      : 'bg-white text-foreground-700 border border-gray-200 hover:border-primary-300 hover:bg-primary-50/30'
                  }`}
                >
                  {selected && <i className="ri-check-line text-xs"></i>}
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* 技能标签 */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-foreground-700">技能标签</label>
            <span className="text-[11px] text-foreground-400">已添加 {(formData.skill_tags || []).length} 个标签</span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="按回车添加技能标签，如：React, AIGC, 视频剪辑, Blender"
                className="w-full px-4 py-2 rounded-xl border border-gray-200 bg-gray-50/50 text-xs text-foreground-950 outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all h-[38px]"
              />
            </div>
            <button
              type="button"
              onClick={handleAddTag}
              className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-xs font-medium text-foreground-800 transition-all cursor-pointer shrink-0 h-[38px]"
            >
              添加标签
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(formData.skill_tags || []).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50/80 text-primary-700 border border-primary-100 text-xs font-medium shadow-2xs"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="hover:text-rose-600 transition-colors cursor-pointer text-sm leading-none"
                  title="删除标签"
                >
                  &times;
                </button>
              </span>
            ))}
            {(formData.skill_tags || []).length === 0 && (
              <p className="text-xs text-foreground-400 italic">尚未添加自定义技能标签</p>
            )}
          </div>
        </div>
      </div>

      {/* 模块4：授权联系方式信息 (带验证码与 Token 换取) */}
      <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-white to-gray-50/60 border border-gray-200/80 shadow-2xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-200/60">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center font-semibold text-sm">
              <i className="ri-shield-user-line"></i>
            </div>
            <h2 className="text-sm font-bold text-foreground-900">授权联系方式信息</h2>
          </div>
          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200/60 px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1">
            <i className="ri-lock-line text-xs"></i> 仅认证企业可查阅
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
          {/* 微信号 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-foreground-700">联系微信</label>
              {(currentDisplay?.contact_wechat_id_masked || currentDisplay?.wechat_id) && (
                <span className="text-[10px] text-foreground-400">
                  已有: {currentDisplay?.contact_wechat_id_masked || currentDisplay?.wechat_id}
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                value={formData.wechat_id || ''}
                onChange={(e) => setFormData({ ...formData, wechat_id: e.target.value })}
                placeholder={currentDisplay?.contact_wechat_id_masked || '请输入微信号'}
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 h-[40px]"
              />
            </div>
          </div>

          {/* 联系电话 + 验证码换取 Token */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-foreground-700">联系电话</label>
              {formData.phone_verification_token ? (
                <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill"></i> 已获得 Token
                </span>
              ) : (currentDisplay?.contact_phone_masked || currentDisplay?.phone) ? (
                <span className="text-[10px] text-foreground-400">
                  已有: {currentDisplay?.contact_phone_masked || currentDisplay?.phone}
                </span>
              ) : null}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.phone || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    phone: val,
                    phone_verification_token: prev.phone_verification_token && val !== prev.phone ? undefined : prev.phone_verification_token,
                  }));
                }}
                placeholder={currentDisplay?.contact_phone_masked || '请输入手机号'}
                className="flex-1 min-w-0 px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 h-[40px]"
              />
              <SendCodeButton
                onSend={handleSendPhoneCode}
                disabled={!formData.phone?.trim()}
                sendingText="发送中"
                className="px-3.5 py-2 shrink-0 text-xs font-semibold rounded-xl bg-primary-50 text-primary-600 border border-primary-200/60 hover:bg-primary-100 disabled:opacity-50 transition-all cursor-pointer h-[40px]"
              />
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value)}
                placeholder="请输入短信验证码"
                className="flex-1 min-w-0 px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 h-[38px]"
              />
              <button
                type="button"
                onClick={handleVerifyPhoneCode}
                disabled={verifyingPhone || !phoneCode.trim()}
                className="px-4 py-2 shrink-0 text-xs font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-all cursor-pointer h-[38px]"
              >
                {verifyingPhone ? '校验中' : '校验'}
              </button>
            </div>
          </div>

          {/* 联系邮箱 + 验证码换取 Token */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-foreground-700">联系邮箱</label>
              {formData.email_verification_token ? (
                <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill"></i> 已获得 Token
                </span>
              ) : (currentDisplay?.contact_email_masked || currentDisplay?.email) ? (
                <span className="text-[10px] text-foreground-400">
                  已有: {currentDisplay?.contact_email_masked || currentDisplay?.email}
                </span>
              ) : null}
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    email: val,
                    email_verification_token: prev.email_verification_token && val !== prev.email ? undefined : prev.email_verification_token,
                  }));
                }}
                placeholder={currentDisplay?.contact_email_masked || '请输入电子邮箱'}
                className="flex-1 min-w-0 px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 h-[40px]"
              />
              <SendCodeButton
                onSend={handleSendEmailCode}
                disabled={!formData.email?.trim()}
                sendingText="发送中"
                className="px-3.5 py-2 shrink-0 text-xs font-semibold rounded-xl bg-primary-50 text-primary-600 border border-primary-200/60 hover:bg-primary-100 disabled:opacity-50 transition-all cursor-pointer h-[40px]"
              />
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value)}
                placeholder="请输入邮箱验证码"
                className="flex-1 min-w-0 px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 h-[38px]"
              />
              <button
                type="button"
                onClick={handleVerifyEmailCode}
                disabled={verifyingEmail || !emailCode.trim()}
                className="px-4 py-2 shrink-0 text-xs font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-all cursor-pointer h-[38px]"
              >
                {verifyingEmail ? '校验中' : '校验'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 模块5：履约简介与作品集 */}
      <div className="p-5 sm:p-6 rounded-2xl bg-white border border-gray-100 shadow-2xs space-y-6">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
          <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center font-semibold text-sm">
            <i className="ri-gallery-line"></i>
          </div>
          <h2 className="text-sm font-bold text-foreground-900">详细履约说明与作品展示</h2>
        </div>

        {/* 个人简介 */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            个人 / 团队简介 <span className="text-rose-500">*</span>
          </label>
          <textarea
            rows={4}
            value={formData.intro || ''}
            onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
            placeholder="请详细描述您的工作背景、核心技术优势、团队实力以及过往优质成功案例..."
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50 text-xs text-foreground-950 placeholder-foreground-400 outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all resize-y"
          ></textarea>
        </div>

        {/* 服务范围 */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            具体服务范围说明 <span className="text-rose-500">*</span>
          </label>
          <textarea
            rows={3}
            value={formData.service_scope || ''}
            onChange={(e) => setFormData({ ...formData, service_scope: e.target.value })}
            placeholder="阐述具体可承接的工作范围、合作流程、交付件物料以及质量服务标准..."
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50 text-xs text-foreground-950 placeholder-foreground-400 outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all resize-y"
          ></textarea>
        </div>

        {/* 代表作品集编辑 */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="block text-xs font-bold text-foreground-800">
                代表作品集 <span className="text-foreground-400 font-normal">({(formData.portfolio || []).length} 部)</span>
              </label>
              <p className="text-[11px] text-foreground-400 mt-0.5">上传展现您专业能力的图片或视频项目，提升合作意向</p>
            </div>
            <button
              type="button"
              onClick={handleAddPortfolioItem}
              className="px-3.5 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-add-line text-sm"></i>
              添加代表作品
            </button>
          </div>

          <div className="space-y-4">
            {(formData.portfolio || []).map((item, idx) => (
              <div key={idx} className="p-5 rounded-2xl bg-gray-50/70 border border-gray-200/80 relative space-y-4 transition-all hover:border-gray-300">
                <div className="flex items-center justify-between border-b border-gray-200/60 pb-2.5">
                  <span className="text-xs font-bold text-primary-700 bg-primary-50 border border-primary-100 px-2.5 py-0.5 rounded-full">
                    作品项 #{idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemovePortfolioItem(idx)}
                    className="px-2.5 py-1 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 text-xs font-medium transition-all flex items-center gap-1 cursor-pointer"
                    title="删除此作品"
                  >
                    <i className="ri-delete-bin-line"></i>
                    删除
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1">作品名称 <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handlePortfolioChange(idx, 'name', e.target.value)}
                      placeholder="请输入作品标题"
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 h-[38px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1">作品文件类型</label>
                    <Select
                      options={PORTFOLIO_CATEGORY_OPTIONS}
                      value={item.attachments?.category || 'PORTFOLIO_IMAGE'}
                      onChange={(val) => {
                        setFormData((prev) => {
                          const list = [...(prev.portfolio || [])];
                          list[idx] = {
                            ...list[idx],
                            attachments: {
                              ...(list[idx].attachments || { sort_no: idx }),
                              category: String(val),
                            },
                          };
                          return { ...prev, portfolio: list };
                        });
                      }}
                      size="sm"
                      selectClassName="rounded-xl text-xs h-[38px] border-gray-200 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <ImageUploadBox
                    label="作品资源文件"
                    scene="PROFILE"
                    isVideo={item.attachments?.category === 'PORTFOLIO_VIDEO'}
                    accept={item.attachments?.category === 'PORTFOLIO_VIDEO' ? 'video/*' : 'image/*,video/*'}
                    placeholder={item.attachments?.category === 'PORTFOLIO_VIDEO' ? '点击或拖拽上传视频作品 (支持 MP4, MOV 等)' : '点击或拖拽上传作品代表图片/视频'}
                    subPlaceholder={item.attachments?.category === 'PORTFOLIO_VIDEO' ? '建议选择清晰、无水印的案例视频' : '建议图片尺寸清晰、主视觉明显'}
                    value={(item as EditablePortfolioItem)._tempPreviewUrl || item.attachments?.upload_asset_id || ''}
                    onChange={(assetId, file) => {
                      setFormData((prev) => {
                        const list = [...(prev.portfolio || [])];
                        if (!assetId) {
                          list[idx] = {
                            ...list[idx],
                            attachments: undefined,
                            _tempPreviewUrl: undefined,
                          };
                        } else {
                          const isVid = file
                            ? (file.type.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv|flv|m4v)$/i.test(file.name))
                            : list[idx].attachments?.category === 'PORTFOLIO_VIDEO';
                          const cat = isVid ? 'PORTFOLIO_VIDEO' : (list[idx].attachments?.category || 'PORTFOLIO_IMAGE');

                          list[idx] = {
                            ...list[idx],
                            attachments: {
                              upload_asset_id: assetId,
                              category: cat,
                              sort_no: idx,
                              display_name: file?.name || list[idx].name || '作品文件',
                            },
                          };
                        }
                        return { ...prev, portfolio: list };
                      });
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1">作品简要说明</label>
                  <textarea
                    rows={2}
                    value={item.intro || ''}
                    onChange={(e) => handlePortfolioChange(idx, 'intro', e.target.value)}
                    placeholder="简要说明此作品的技术亮点、项目成果或具体负责模块..."
                    className="w-full px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 resize-y"
                  />
                </div>
              </div>
            ))}
            {(formData.portfolio || []).length === 0 && (
              <div className="p-8 text-center rounded-2xl bg-gray-50/60 border border-dashed border-gray-200 text-xs text-foreground-400 space-y-2">
                <i className="ri-folder-add-line text-3xl text-gray-300 block"></i>
                <p className="font-medium text-foreground-600">暂未添加作品项目</p>
                <p className="text-[11px]">添加作品可以极大地展示您的专业水准并吸引需求方主动联系</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部悬浮/操作按钮 */}
      <div className="pt-5 pb-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200/80">
        <div className="text-xs text-foreground-500">
          {onOpenAgreement && (
            <span>
              保存或提交即代表您已阅读并同意{' '}
              <button
                type="button"
                onClick={onOpenAgreement}
                className="text-primary-600 hover:text-primary-700 underline font-medium cursor-pointer"
              >
                《AI创作服务供给方入驻及需求竞标服务协议_草案》
              </button>
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-xl text-xs font-semibold text-foreground-600 hover:bg-gray-100 transition-all cursor-pointer"
          >
            取消修改
          </button>
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={savingDraft}
            className="px-6 py-2.5 rounded-xl border border-primary-300 text-primary-700 bg-white hover:bg-primary-50 text-xs font-semibold transition-all shadow-2xs cursor-pointer disabled:opacity-50"
          >
            {savingDraft ? '保存中...' : '保存草稿'}
          </button>
          <button
            type="button"
            onClick={handleSubmitProfile}
            disabled={submitting}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-xs font-semibold transition-all shadow-sm cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
          >
            <i className="ri-send-plane-fill text-sm"></i>
            {submitting ? '提交中...' : '保存并提交审核'}
          </button>
        </div>
      </div>
    </div>
  );
}
